import { cn } from "@designs-by-maryam/ui/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { Quote, Star } from "lucide-react";
import { motion } from "motion/react";
import { useMemo } from "react";
import { testimonials as staticTestimonials } from "@/data/testimonials";
import { useSafeMotion } from "@/lib/motion";
import { featuredTestimonialsQueryOptions } from "@/lib/query-options";

type TestimonialsSectionProps = {
	className?: string;
	/** Maximum number of testimonials to display */
	maxItems?: number;
	/** Title for the section */
	title?: string;
	/** Subtitle for the section */
	subtitle?: string;
	/** Pre-resolved testimonials from Puck */
	resolvedTestimonials?: Array<{
		id: string;
		clientName: string;
		clientRole?: string;
		companyName?: string;
		content: string;
		rating?: number;
	}>;
};

/**
 * TestimonialsSection Component
 *
 * Displays client testimonials in a responsive grid layout.
 * Fetches from CMS with static fallback.
 *
 * Features:
 * - CMS-driven content with static fallback
 * - Responsive grid (3 cols desktop, 2 tablet, 1 mobile)
 * - Staggered entrance animations
 * - Quote styling with decorative elements
 */
export function TestimonialsSection({
	className,
	maxItems = 6,
	title = "What Our Clients Say",
	subtitle = "Trusted by homeowners and businesses across Bahrain",
	resolvedTestimonials,
}: TestimonialsSectionProps) {
	const { shouldReduce, slideUp, viewportOnce, scaleIn } = useSafeMotion();

	// Fetch testimonials from CMS (skip if Puck-resolved)
	const { data: cmsData, isError } = useQuery({
		...featuredTestimonialsQueryOptions(),
		enabled: !resolvedTestimonials || resolvedTestimonials.length === 0,
		retry: 1,
		staleTime: 60 * 60 * 1000, // 1 hour
	});

	// Transform data to display format
	const testimonials = useMemo(() => {
		// Priority 1: Puck-resolved testimonials
		if (resolvedTestimonials && resolvedTestimonials.length > 0) {
			return resolvedTestimonials.slice(0, maxItems).map((t) => ({
				id: t.id,
				quote: t.content,
				name: t.clientName,
				title: t.clientRole ?? "",
				company: t.companyName ?? "",
				rating: t.rating ?? 5,
			}));
		}

		// Priority 2: CMS testimonials
		if (!isError && cmsData?.data && cmsData.data.length > 0) {
			return cmsData.data.slice(0, maxItems).map((t) => ({
				id: t.id,
				quote: t.content,
				name: t.client_name,
				title: t.client_role ?? "",
				company: t.company_name ?? "",
				rating: 5, // Default rating
			}));
		}

		// Priority 3: Static fallback
		return staticTestimonials.slice(0, maxItems).map((t, idx) => ({
			id: `static-${idx}`,
			quote: t.quote,
			name: t.name,
			title: t.title,
			company: "",
			rating: 5,
		}));
	}, [resolvedTestimonials, cmsData, isError, maxItems]);

	// Don't render if no testimonials
	if (testimonials.length === 0) {
		return null;
	}

	return (
		<section
			aria-labelledby="testimonials-heading"
			className={cn("py-16 md:py-24", className)}
			data-testid="testimonials-section"
		>
			<div className="container mx-auto px-4 md:px-6">
				{/* Section Header */}
				<motion.div
					className="mb-12 text-center"
					initial={slideUp.initial}
					transition={slideUp.transition}
					viewport={viewportOnce}
					whileInView={slideUp.animate}
				>
					<h2
						className="bg-gradient-to-br from-foreground via-foreground to-foreground/70 bg-clip-text font-bold text-3xl text-transparent tracking-tight md:text-4xl lg:text-5xl"
						id="testimonials-heading"
						style={{ letterSpacing: "-0.02em" }}
					>
						{title}
					</h2>
					<p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground">
						{subtitle}
					</p>
				</motion.div>

				{/* Testimonials Grid */}
				<div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
					{testimonials.map((testimonial, index) => (
						<motion.article
							className="group relative overflow-hidden rounded-2xl border border-border/50 bg-card p-6 shadow-sm transition-all duration-300 hover:border-primary/20 hover:shadow-lg hover:shadow-primary/5"
							initial={scaleIn.initial}
							key={testimonial.id}
							transition={{
								...scaleIn.transition,
								delay: shouldReduce ? 0 : index * 0.1,
							}}
							viewport={viewportOnce}
							whileInView={scaleIn.animate}
						>
							{/* Quote Icon */}
							<div className="absolute -top-2 -right-2 text-primary/5 transition-colors group-hover:text-primary/10">
								<Quote className="h-24 w-24" />
							</div>

							{/* Rating Stars */}
							<div className="mb-4 flex gap-1">
								{Array.from({ length: testimonial.rating }).map((_, i) => (
									<Star
										className="h-4 w-4 fill-gold text-gold"
										// biome-ignore lint/suspicious/noArrayIndexKey: Static star rating
										key={i}
									/>
								))}
							</div>

							{/* Quote Content */}
							<blockquote className="relative mb-6">
								<p className="text-muted-foreground leading-relaxed">
									"{testimonial.quote}"
								</p>
							</blockquote>

							{/* Client Info */}
							<footer className="flex items-center gap-3 border-t border-border/50 pt-4">
								{/* Avatar Placeholder */}
								<div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 font-semibold text-primary text-sm">
									{testimonial.name
										.split(" ")
										.map((n) => n[0])
										.join("")
										.slice(0, 2)}
								</div>
								<div className="min-w-0 flex-1">
									<cite className="not-italic font-semibold text-foreground text-sm">
										{testimonial.name}
									</cite>
									{testimonial.title && (
										<p className="truncate text-muted-foreground text-xs">
											{testimonial.title}
											{testimonial.company && `, ${testimonial.company}`}
										</p>
									)}
								</div>
							</footer>
						</motion.article>
					))}
				</div>
			</div>
		</section>
	);
}

export default TestimonialsSection;
